import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Modal, Form, Tab, Nav, Badge } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, Eye, Download, FileText, Copy, ChevronLeft, ChevronRight, Search, X, Mail } from 'lucide-react';
import apiService from '../services/api';
import { StoricoReferti, Referto, Clienti, BozzaItem } from '../types';
import RefertoPDF from '../components/RefertoPDF';
import EmailComposeModal from '../components/EmailComposeModal';
import { useSpecialty } from '../contexts/SpecialtyContext';

const Referti: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { specialtyId } = useParams<{ specialtyId: string }>();
  const { getSpecialtyInfo, selectedSpecialty } = useSpecialty();
  const [referti, setReferti] = useState<StoricoReferti[]>([]); // Referti paginati dal server
  const [bozze, setBozze] = useState<BozzaItem[]>([]);
  const [clienti, setClienti] = useState<Clienti[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReferto, setEditingReferto] = useState<StoricoReferti | null>(null);

  // Stati per la paginazione server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10; // Deve corrispondere al page_size del backend

  // Stati per la paginazione server-side delle bozze
  const [bozzePage, setBozzePage] = useState(1);
  const [bozzeTotalPages, setBozzeTotalPages] = useState(1);
  const [bozzeCount, setBozzeCount] = useState(0);
  const bozzePageSize = 10; // Deve corrispondere al page_size del backend

  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedReferto, setSelectedReferto] = useState<StoricoReferti | null>(null);
  const [selectedRefertoCompleto, setSelectedRefertoCompleto] = useState<StoricoReferti | null>(null);
  const [selectedBozza, setSelectedBozza] = useState<BozzaItem | null>(null);
  const [activeTab, setActiveTab] = useState('referti');
  const [loadingPDF, setLoadingPDF] = useState(false);
  
  // Stati per la modale di conferma eliminazione
  const [emailReferto, setEmailReferto] = useState<StoricoReferti | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState<{id: number, isBozza: boolean, nome: string} | null>(null);

  // Stati per la ricerca
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Ottieni le informazioni della specialistica per la navigazione
  const specialtyInfo = getSpecialtyInfo(selectedSpecialty);

  // Funzione per caricare i referti con paginazione server-side
  const loadReferti = useCallback(async (page: number) => {
    try {
      setLoading(true);
      
      // Determina il valore della specializzazione da passare alle API
      let specialisticaValue: number | undefined;
      if (specialtyId) {
        specialisticaValue = parseInt(specialtyId);
      } else {
        const specialisticaFromStorage = localStorage.getItem('selectedSpecialty');
        if (specialisticaFromStorage) {
          specialisticaValue = parseInt(specialisticaFromStorage);
        }
      }
      
      // Carica i referti con paginazione server-side
      const refertiRes = await apiService.getReferti(specialisticaValue, page, pageSize);
      
      // Usa i dati di paginazione dall'API
      setReferti(refertiRes?.results || []);
      setTotalCount(refertiRes?.count || 0);
      setTotalPages(refertiRes?.num_pages || 1);
      setCurrentPage(refertiRes?.current_page || page);
  
    } catch (error) {
      console.error('Errore nel caricamento dei referti:', error);
      toast.error('Errore nel caricamento dei referti');
      setReferti([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [specialtyId, pageSize]);

  // Funzione per caricare le bozze (caricamento separato)
  const loadBozze = useCallback(async (page: number = 1) => {
    try {
      let specialisticaValue: number | undefined;
      if (specialtyId) {
        specialisticaValue = parseInt(specialtyId);
      } else {
        const specialisticaFromStorage = localStorage.getItem('selectedSpecialty');
        if (specialisticaFromStorage) {
          specialisticaValue = parseInt(specialisticaFromStorage);
        }
      }
      
      const bozzeRes = await apiService.getBozzeReferti(specialisticaValue, page);
      setBozze(bozzeRes?.data || []);
      setBozzeCount(bozzeRes?.count || 0);
      setBozzeTotalPages(bozzeRes?.num_pages || 1);
      setBozzePage(bozzeRes?.current_page || page);
    } catch (error) {
      console.error('Errore nel caricamento delle bozze:', error);
      setBozze([]);
      setBozzeCount(0);
      setBozzeTotalPages(1);
    }
  }, [specialtyId]);

  // Funzione per caricare tutti i dati inizialmente
  const loadData = useCallback(async () => {
    await Promise.all([
      loadReferti(1),
      loadBozze()
    ]);
  }, [loadReferti, loadBozze]);

  useEffect(() => {
    if (specialtyId) {
      loadData();
    }
  }, [specialtyId, location.key, loadData]); // location.key cambia ad ogni navigazione

  // Funzioni per la paginazione server-side
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      loadReferti(newPage);
    }
  };

  const handleBozzePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= bozzeTotalPages && newPage !== bozzePage) {
      loadBozze(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Funzione per la ricerca
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    try {
      setLoading(true);
      setIsSearching(true);
      
      let specialisticaValue: number | undefined;
      if (specialtyId) {
        specialisticaValue = parseInt(specialtyId);
      } else {
        const specialisticaFromStorage = localStorage.getItem('selectedSpecialty');
        if (specialisticaFromStorage) {
          specialisticaValue = parseInt(specialisticaFromStorage);
        }
      }
      
      if (!specialisticaValue) {
        toast.error('Specialistica non trovata');
        return;
      }
      
      const results = await apiService.searchReferti(specialisticaValue, searchQuery);
      setReferti(results || []);
      setTotalCount(results?.length || 0);
      setTotalPages(1); // Nessuna paginazione durante la ricerca
      setCurrentPage(1);
    } catch (error) {
      console.error('Errore nella ricerca:', error);
      toast.error('Errore nella ricerca dei referti');
      setReferti([]);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per cancellare la ricerca
  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    loadReferti(1);
  };

  const handleEdit = (referto: StoricoReferti | BozzaItem, isBozza: boolean = false) => {
    if (isBozza) {
      // Per le bozze, naviga verso la pagina NuovoReferto con i dati della bozza
      const bozza = referto as BozzaItem;
      
      if (!specialtyId) {
        toast.error('Errore: specialistica non specificata');
        return;
      }
      
      const specialtyInfo = getSpecialtyInfo(specialtyId);
      const specialtyName = specialtyInfo?.name.toLowerCase();
      
      if (!specialtyName) {
        toast.error('Errore: specialistica non trovata');
        return;
      }
      
      navigate(`/referti/${specialtyName}/nuovo`, { 
        state: { 
          editBozza: true, 
          bozzaId: bozza.id,
          clearDraft: false 
        } 
      });
    } else {
      setEditingReferto(referto as StoricoReferti);
      setShowModal(true);
    }
  };

  const handleDelete = (id: number, isBozza: boolean = false, nome: string = '') => {
    setDeleteItem({ id, isBozza, nome });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    
    try {
      if (deleteItem.isBozza) {
        await apiService.deleteBozza(deleteItem.id);
        // Ricarica la pagina corrente delle bozze (il backend ripiega sull'ultima pagina valida)
        loadBozze(bozzePage);
      } else {
        await apiService.deleteReferto(deleteItem.id);
        // Ricarica i referti dalla prima pagina
        loadReferti(1);
      }
      toast.success('Referto eliminato con successo');
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setShowDeleteModal(false);
      setDeleteItem(null);
    }
  };

  const handleView = async (referto: StoricoReferti | BozzaItem, isBozza: boolean = false) => {
    if (isBozza) {
      setSelectedBozza(referto as BozzaItem);
      setSelectedReferto(null);
      setSelectedRefertoCompleto(null);
    } else {
      setSelectedReferto(referto as StoricoReferti);
      setSelectedBozza(null);
      setLoadingPDF(true);
      
      try {
        // Carica i dati completi del referto con referto_detail
        console.log('🔍 Caricamento dati completi referto ID:', referto.id);
        const refertoCompleto = await apiService.getReferto(referto.id);
        console.log('✅ Dati completi ricevuti:', refertoCompleto);
        setSelectedRefertoCompleto(refertoCompleto);
      } catch (error) {
        console.error('❌ Errore nel caricamento dei dati completi:', error);
        toast.error('Errore nel caricamento dei dettagli del referto');
        setSelectedReferto(null);
        setLoadingPDF(false);
        return;
      } finally {
        setLoadingPDF(false);
      }
    }
    setShowPDFModal(true);
  };

  const handleDownload = async (id: number, isBozza: boolean = false) => {
    try {
      if (isBozza) {
        const newReferto = await apiService.generateRefertoFromBozza(id);
        // Dopo la generazione, scarica il PDF del nuovo referto
        if (newReferto && newReferto.id) {
          const blob = await apiService.downloadRefertoPDF(newReferto.id);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `referto_${newReferto.id}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
        toast.success('Referto generato e scaricato con successo');
      } else {
        const blob = await apiService.downloadRefertoPDF(id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `referto_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (editingReferto) {
        await apiService.updateReferto(editingReferto.id, formData);
      } else {
        await apiService.createReferto(formData);
      }
      
      toast.success('Referto salvato con successo');
      setShowModal(false);
      // Ricarica i referti dalla prima pagina
      loadReferti(1);
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  const handleGenerateFromBozza = async (bozzaId: number) => {
    try {
      await apiService.generateRefertoFromBozza(bozzaId);
      toast.success('Referto generato con successo dalla bozza');
      // Ricarica referti e bozze
      loadReferti(1);
      loadBozze(bozzePage);
    } catch (error) {
      toast.error('Errore durante la generazione del referto');
    }
  };

  const handleDuplicate = async (referto: StoricoReferti) => {
    try {
      await apiService.duplicateReferto(referto.id);
    
      toast.success('Referto duplicato con successo');
      // Ricarica i referti dalla prima pagina
      loadReferti(1);
    } catch (error) {
      console.error('❌ [DUPLICATE] Errore durante la duplicazione:', error);
      console.error('❌ [DUPLICATE] Dettagli errore:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: (error as any)?.response?.data,
        status: (error as any)?.response?.status
      });
      toast.error('Errore durante la duplicazione del referto');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <Container fluid>
          <div className="text-center p-5">
            <div className="modern-spinner mx-auto mb-3"></div>
            <p className="text-muted">Caricamento referti...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Container fluid>
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>Gestione Referti</h1>
              <p className="text-muted">Visualizza e gestisci tutti i referti</p>
            </div>
            <Button 
              variant="primary" 
              onClick={() => navigate(`/referti/${specialtyInfo?.name.toLowerCase()}/nuovo`, { state: { clearDraft: true } })}
              className="btn-modern d-flex align-items-center gap-2"
            >
              <Plus size={20} />
              Nuovo Referto
            </Button>
          </div>
        </div>

        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'referti')}>
          <section className="inv-stats">
            <div className="inv-stat">
              <span>Referti totali</span>
              <strong>{totalCount}</strong>
            </div>
            <div className="inv-stat">
              <span>Bozze in sospeso</span>
              <strong>{bozzeCount}</strong>
            </div>
            <div className="inv-stat">
              <span>{isSearching ? 'Risultati della ricerca' : 'In questa pagina'}</span>
              <strong>{referti.length}</strong>
            </div>
          </section>

          <Nav variant="pills" className="seg mb-3">
            <Nav.Item>
              <Nav.Link eventKey="referti">
                <FileText size={18} />
                <span>Referti</span>
                <span className="seg-count">{totalCount}</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="bozze">
                <Edit size={18} />
                <span>Bozze</span>
                <span className="seg-count">{bozzeCount}</span>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === 'referti' && (
            <Form onSubmit={handleSearch} className="inv-search">
              <div className="inv-search-field">
                <Search size={18} />
                <Form.Control
                  type="text"
                  placeholder="Cerca per nome, cognome, paziente..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {isSearching && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={clearSearch}
                    className="inv-search-clear"
                    title="Cancella ricerca"
                  >
                    <X size={18} />
                  </Button>
                )}
              </div>
              <Button type="submit" variant="primary">
                <Search size={16} />
                Cerca
              </Button>
            </Form>
          )}

          <Tab.Content>
            <Tab.Pane eventKey="referti">
              {isSearching && (
                <div className="inv-note">
                  <span>
                    <Search size={16} className="me-2" />
                    Risultati ricerca per: <strong>"{searchQuery}"</strong> - {referti.length} risultati trovati
                  </span>
                  <Button variant="outline-secondary" size="sm" onClick={clearSearch}>
                    Mostra tutti i referti
                  </Button>
                </div>
              )}

              <div className="inv-list is-ref">
                <div className="inv-head">
                  <span>Referto</span>
                  <span>Paziente</span>
                  <span>Cliente</span>
                  <span />
                </div>

                {referti.length === 0 && (
                  <div className="set-empty m-3">
                    <FileText size={34} />
                    <strong>Nessun referto</strong>
                    <span>I referti che generi compariranno qui.</span>
                  </div>
                )}

                {referti.map((storicoReferto, index) => (
                  <article key={storicoReferto.id} className="inv-row" style={{ animationDelay: `${index * 0.04}s` }}>
                    <div className="inv-num">
                      <strong>#{storicoReferto.referto.numero}</strong>
                      <small>{new Date(storicoReferto.data).toLocaleDateString()}</small>
                    </div>

                    <div className="inv-client">
                      <span className="inv-avatar">
                        {(storicoReferto.referto.paziente?.nome?.[0] || '?').toUpperCase()}
                      </span>
                      <div>
                        <strong>{storicoReferto.referto.paziente?.nome || 'N/A'}</strong>
                        <small>Paziente</small>
                      </div>
                    </div>

                    <div className="inv-client">
                      <span className="inv-avatar is-alt">
                        {`${storicoReferto.clienti?.nome?.[0] || ''}${storicoReferto.clienti?.cognome?.[0] || ''}`.toUpperCase() || '?'}
                      </span>
                      <div>
                        <strong>{storicoReferto.clienti?.nome || 'N/A'} {storicoReferto.clienti?.cognome || ''}</strong>
                        <small>Cliente</small>
                      </div>
                    </div>

                    <div className="inv-actions">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleView(storicoReferto)}
                        className="btn-sm-modern"
                        title="Visualizza"
                      >
                        <Eye size={14} />
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleDownload(storicoReferto.id)}
                        className="btn-sm-modern"
                        title="Scarica PDF"
                      >
                        <Download size={14} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleDuplicate(storicoReferto)}
                        className="btn-sm-modern"
                        title="Duplica referto"
                      >
                        <Copy size={14} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setEmailReferto(storicoReferto)}
                        className="btn-sm-modern"
                        title="Invia per email"
                      >
                        <Mail size={14} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(storicoReferto.id, false, `Referto #${storicoReferto.referto.numero}`)}
                        className="btn-sm-modern"
                        title="Elimina"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </article>
                ))}

                {/* Componente di paginazione - nascosto durante la ricerca */}
                {totalPages > 1 && !isSearching && (
                  <div className="inv-foot">
                    <div className="text-muted small">
                      Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} di {totalCount} referti
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="btn-sm-modern"
                      >
                        <ChevronLeft size={14} />
                      </Button>

                      <div className="d-flex gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                          // Mostra solo alcune pagine intorno alla pagina corrente
                          const showPage = pageNum === 1 ||
                                         pageNum === totalPages ||
                                         (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                          if (!showPage) {
                            // Mostra "..." se ci sono pagine nascoste
                            if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                              return <span key={pageNum} className="px-1 small">...</span>;
                            }
                            return null;
                          }

                          return (
                            <Button
                              key={pageNum}
                              variant={pageNum === currentPage ? "primary" : "outline-secondary"}
                              size="sm"
                              onClick={() => handlePageChange(pageNum)}
                              className="btn-sm-modern px-2"
                              style={{ minWidth: '32px' }}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="btn-sm-modern"
                      >
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="bozze">
              <div className="inv-list is-bozze">
                <div className="inv-head">
                  <span>Bozza</span>
                  <span>Cliente</span>
                  <span>Paziente</span>
                  <span />
                </div>

                {bozze.length === 0 && (
                  <div className="set-empty m-3">
                    <Edit size={34} />
                    <strong>Nessuna bozza</strong>
                    <span>Le bozze che salvi compariranno qui.</span>
                  </div>
                )}

                {bozze.map((bozza, index) => (
                  <article key={bozza.id} className="inv-row" style={{ animationDelay: `${index * 0.04}s` }}>
                    <div className="inv-num">
                      <strong>{bozza.titolo || 'Bozza senza nome'}</strong>
                      <small>Modificata il {bozza.data_modifica?.split(' ')[0] || 'N/A'}</small>
                    </div>

                    <div className="inv-client">
                      <span className="inv-avatar is-alt">
                        {`${bozza.cliente_nome?.[0] || ''}${bozza.cliente_cognome?.[0] || ''}`.toUpperCase() || '?'}
                      </span>
                      <div>
                        <strong>
                          {bozza.ha_cliente && bozza.cliente_nome && bozza.cliente_cognome
                            ? `${bozza.cliente_nome} ${bozza.cliente_cognome}`.trim()
                            : 'ND'
                          }
                        </strong>
                        <small>Cliente</small>
                      </div>
                    </div>

                    <div className="inv-client">
                      <span className="inv-avatar">
                        {(bozza.paziente_nome?.[0] || '?').toUpperCase()}
                      </span>
                      <div>
                        <strong>
                          {bozza.paziente_nome && bozza.paziente_nome.trim() !== ''
                            ? bozza.paziente_nome
                            : 'N/D'
                          }
                        </strong>
                        <small>Paziente</small>
                      </div>
                    </div>

                    <div className="inv-actions">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleView(bozza, true)}
                        className="btn-sm-modern"
                        title="Visualizza"
                      >
                        <Eye size={14} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleEdit(bozza, true)}
                        className="btn-sm-modern"
                        title="Modifica"
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleGenerateFromBozza(bozza.id)}
                        className="btn-sm-modern"
                        title="Genera Referto"
                      >
                        <FileText size={14} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(bozza.id, true, bozza.titolo || 'Bozza senza nome')}
                        className="btn-sm-modern"
                        title="Elimina"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </article>
                ))}

                {bozzeTotalPages > 1 && (
                  <div className="inv-foot">
                    <div className="text-muted small">
                      Mostrando {((bozzePage - 1) * bozzePageSize) + 1} - {Math.min(bozzePage * bozzePageSize, bozzeCount)} di {bozzeCount} bozze
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleBozzePageChange(bozzePage - 1)}
                        disabled={bozzePage === 1}
                        className="btn-sm-modern"
                      >
                        <ChevronLeft size={14} />
                      </Button>

                      <div className="d-flex gap-1">
                        {Array.from({ length: bozzeTotalPages }, (_, i) => i + 1).map((pageNum) => {
                          const showPage = pageNum === 1 ||
                                         pageNum === bozzeTotalPages ||
                                         (pageNum >= bozzePage - 1 && pageNum <= bozzePage + 1);

                          if (!showPage) {
                            if (pageNum === bozzePage - 2 || pageNum === bozzePage + 2) {
                              return <span key={pageNum} className="px-1 small">...</span>;
                            }
                            return null;
                          }

                          return (
                            <Button
                              key={pageNum}
                              variant={pageNum === bozzePage ? "primary" : "outline-secondary"}
                              size="sm"
                              onClick={() => handleBozzePageChange(pageNum)}
                              className="btn-sm-modern px-2"
                              style={{ minWidth: '32px' }}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleBozzePageChange(bozzePage + 1)}
                        disabled={bozzePage === bozzeTotalPages}
                        className="btn-sm-modern"
                      >
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* Modal per creazione/modifica */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" className="modern-modal">
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2">
              <Edit size={20} />
              {editingReferto ? 'Modifica Referto' : 'Nuovo Referto'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <RefertoForm
              referto={editingReferto?.referto || undefined}
              clienti={clienti}
              onSave={handleSave}
            />
          </Modal.Body>
        </Modal>

        {/* Modal per visualizzazione PDF */}
        <Modal 
          show={showPDFModal} 
          onHide={() => {
            setShowPDFModal(false);
            setSelectedReferto(null);
            setSelectedRefertoCompleto(null);
            setSelectedBozza(null);
            setLoadingPDF(false);
          }} 
          size="xl"
          className="modern-modal referto-preview-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2">
              <Eye size={20} />
              {selectedReferto && 'Anteprima Referto PDF'}
              {selectedBozza && 'Dettagli Bozza'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingPDF && (
              <div className="text-center p-5">
                <div className="modern-spinner mx-auto mb-3"></div>
                <p className="text-muted">Caricamento dati completi del referto...</p>
              </div>
            )}
            {selectedRefertoCompleto && !loadingPDF && (
              <RefertoPDF 
                referto={selectedRefertoCompleto.referto} 
                profile={selectedRefertoCompleto.profile}
                referto_detail={selectedRefertoCompleto.referto_detail}
                clienti={selectedRefertoCompleto.clienti}
                storicoId={selectedRefertoCompleto.id}
              />
            )}
            {selectedReferto && !selectedRefertoCompleto && !loadingPDF && (
              <RefertoPDF 
                referto={selectedReferto.referto} 
                profile={selectedReferto.profile}
                clienti={selectedReferto.clienti}
                storicoId={selectedReferto.id}
              />
            )}
            {selectedBozza && (
              <div className="text-center p-5">
                <h5>Informazioni Bozza</h5>
                <p><strong>ID:</strong> {selectedBozza.id}</p>
                <p><strong>Titolo:</strong> {selectedBozza.titolo}</p>
                <p><strong>Data Modifica:</strong> {selectedBozza.data_modifica}</p>
                <p><strong>Cliente:</strong> {
                  selectedBozza.ha_cliente && selectedBozza.cliente_nome 
                    ? `${selectedBozza.cliente_nome} ${selectedBozza.cliente_cognome}`.trim()
                    : 'ND'
                }</p>
                <p><strong>Paziente:</strong> {
                  selectedBozza.ha_paziente && selectedBozza.paziente_nome 
                    ? `${selectedBozza.paziente_nome}`.trim()
                    : 'ND'
                }</p>
                <div className="mt-3">
                  <p><strong>Status:</strong></p>
                  <div className="d-flex justify-content-center gap-2">
                    {selectedBozza.ha_cliente && <Badge bg="info" className="modern-badge">Cliente ✓</Badge>}
                    {selectedBozza.ha_paziente && <Badge bg="success" className="modern-badge">Paziente ✓</Badge>}
                    {selectedBozza.ha_file_pdf && <Badge bg="warning" className="modern-badge">PDF ✓</Badge>}
                    {selectedBozza.ha_misurazioni && <Badge bg="primary" className="modern-badge">Misurazioni ✓</Badge>}
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>

        {/* Modal per conferma eliminazione */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} className="modern-modal">
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2">
              <Trash2 size={20} className="text-danger" />
              Conferma Eliminazione
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <div className="mb-3">
                <Trash2 size={48} className="text-danger mb-3" />
                <h5>Sei sicuro di voler eliminare questo {deleteItem?.isBozza ? 'bozza' : 'referto'}?</h5>
                {deleteItem?.nome && (
                  <p className="text-muted mb-0">
                    <strong>{deleteItem.nome}</strong>
                  </p>
                )}
              </div>
              <p className="text-muted">
                Questa azione non può essere annullata.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowDeleteModal(false)}
              className="btn-modern"
            >
              Annulla
            </Button>
            <Button 
              variant="danger" 
              onClick={confirmDelete}
              className="btn-modern d-flex align-items-center gap-2"
            >
              <Trash2 size={16} />
              Elimina
            </Button>
          </Modal.Footer>
        </Modal>

        {emailReferto && (
          <EmailComposeModal
            show
            onHide={() => setEmailReferto(null)}
            tipo="referto"
            storicoId={emailReferto.id}
            etichetta={`Referto n. ${emailReferto.referto.numero}`}
            cliente={emailReferto.clienti}
          />
        )}
      </Container>
    </div>
  );
};

// Componente per il form del referto
interface RefertoFormProps {
  referto?: Referto;
  clienti: Clienti[];
  onSave: (data: any) => void;
}

const RefertoForm: React.FC<RefertoFormProps> = ({ referto, clienti, onSave }) => {
  const [formData, setFormData] = useState({
    numero: referto?.numero || '',
    data: referto?.data || new Date().toISOString().split('T')[0],
    paziente_id: referto?.paziente?.id || '',
    medico_referente_id: referto?.medico_referente?.id || '',
    motivo_visita: referto?.motivo_visita || '',
    terapia_in_atto: referto?.terapia_in_atto || '',
    anamnesi: referto?.anamnesi || '',
    conclusioni: referto?.conclusioni || '',
    indicazioni: referto?.indicazioni || '',
    controllo: referto?.controllo || '',
    note: referto?.note || '',
    note_eco: referto?.note_eco || '',
    frequenza_cardiaca: referto?.frequenza_cardiaca || '',
    ritmo_cardiaco: referto?.ritmo_cardiaco || '',
    mucose: referto?.mucose || '',
    trc: referto?.trc || '',
    itto_cardiaco: referto?.itto_cardiaco || '',
    polso_femorale: referto?.polso_femorale || '',
    polso_giugulare: referto?.polso_giugulare || '',
    attivita: referto?.attivita || '',
    tosse: referto?.tosse || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Form onSubmit={handleSubmit} className="modern-form">
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Numero Referto</Form.Label>
            <Form.Control
              type="text"
              value={formData.numero}
              onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Data</Form.Label>
            <Form.Control
              type="date"
              value={formData.data}
              onChange={(e) => setFormData({ ...formData, data: e.target.value })}
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Paziente</Form.Label>
            <Form.Select
              value={formData.paziente_id}
              onChange={(e) => setFormData({ ...formData, paziente_id: e.target.value })}
            >
              <option value="">Seleziona paziente</option>
              {clienti.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nome} {cliente.cognome}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Medico Referente</Form.Label>
            <Form.Control
              type="text"
              value={formData.medico_referente_id}
              onChange={(e) => setFormData({ ...formData, medico_referente_id: e.target.value })}
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label>Motivo Visita</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={formData.motivo_visita}
          onChange={(e) => setFormData({ ...formData, motivo_visita: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Terapia in Atto</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={formData.terapia_in_atto}
          onChange={(e) => setFormData({ ...formData, terapia_in_atto: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Anamnesi</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={formData.anamnesi}
          onChange={(e) => setFormData({ ...formData, anamnesi: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Conclusioni</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={formData.conclusioni}
          onChange={(e) => setFormData({ ...formData, conclusioni: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Indicazioni</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={formData.indicazioni}
          onChange={(e) => setFormData({ ...formData, indicazioni: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Prossimo Controllo</Form.Label>
        <Form.Control
          type="text"
          value={formData.controllo}
          onChange={(e) => setFormData({ ...formData, controllo: e.target.value })}
        />
      </Form.Group>

      <div className="d-flex justify-content-end gap-2">
        <Button 
          type="submit" 
          variant="primary"
          className="btn-modern d-flex align-items-center gap-2"
        >
          <Edit size={16} />
          {referto ? 'Aggiorna' : 'Salva'} Referto
        </Button>
      </div>
    </Form>
  );
};

export default Referti; 